import { Field, ErrorMessage, FormikProps } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { JerseyPickerButton } from '@/components/composite/JerseyPickerButton';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface QuestionImageFieldsProps {
  formik: FormikProps<QuestionFormValues>;
  isEditable: boolean;
  language?: 'en' | 'fr';
}

export function QuestionImageFields({ formik, isEditable, language }: QuestionImageFieldsProps) {
  const { errors, touched, setFieldValue } = formik;

  // If language is specified, show only that language's field
  if (language === 'en') {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_image_en">Question Image URL</Label>
        <div className="flex gap-2">
          <Field
            as={Input}
            id="question_image_en"
            name="question_image_en"
            data-test-id="question-question_image_en"
            placeholder="https://example.com/image.jpg"
            disabled={!isEditable}
            className={
              errors.question_image_en && touched.question_image_en ? 'border-red-500' : ''
            }
          />
          {isEditable && (
            <JerseyPickerButton
              onSelect={url => setFieldValue('question_image_en', url)}
              fieldLabel="Question Image"
            />
          )}
        </div>
        <ErrorMessage name="question_image_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  if (language === 'fr') {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_image_fr">Question Image URL</Label>
        <div className="flex gap-2">
          <Field
            as={Input}
            id="question_image_fr"
            name="question_image_fr"
            data-test-id="question-question_image_fr"
            placeholder="https://example.com/image-fr.jpg"
            disabled={!isEditable}
            className={
              errors.question_image_fr && touched.question_image_fr ? 'border-red-500' : ''
            }
          />
          {isEditable && (
            <JerseyPickerButton
              onSelect={url => setFieldValue('question_image_fr', url)}
              fieldLabel="Question Image"
            />
          )}
        </div>
        <ErrorMessage name="question_image_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  // Fallback: show both languages side-by-side (for backward compatibility)
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_image_en">Question Image URL (English)</Label>
        <div className="flex gap-2">
          <Field
            as={Input}
            id="question_image_en"
            name="question_image_en"
            data-test-id="question-question_image_en"
            placeholder="https://example.com/image.jpg"
            disabled={!isEditable}
            className={
              errors.question_image_en && touched.question_image_en ? 'border-red-500' : ''
            }
          />
          {isEditable && (
            <JerseyPickerButton
              onSelect={url => setFieldValue('question_image_en', url)}
              fieldLabel="Question Image (EN)"
            />
          )}
        </div>
        <ErrorMessage name="question_image_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="question_image_fr">Question Image URL (French)</Label>
        <div className="flex gap-2">
          <Field
            as={Input}
            id="question_image_fr"
            name="question_image_fr"
            data-test-id="question-question_image_fr"
            placeholder="https://example.com/image-fr.jpg"
            disabled={!isEditable}
            className={
              errors.question_image_fr && touched.question_image_fr ? 'border-red-500' : ''
            }
          />
          {isEditable && (
            <JerseyPickerButton
              onSelect={url => setFieldValue('question_image_fr', url)}
              fieldLabel="Question Image (FR)"
            />
          )}
        </div>
        <ErrorMessage name="question_image_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    </div>
  );
}
